'use client';

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface NeedsAttentionItem {
  id: string;
  category: string;
  title: string;
  related: string;
  branch: string;
  owner_name: string | null;
  due_date: string | null;
  due_label: string | null;
  action_needed: string;
  is_urgent: boolean;
  is_resolved: boolean;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
}

export function useNeedsAttention() {
  const [items, setItems] = useState<NeedsAttentionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const supabase = useMemo(() => createClient(), []);
  const channelRef = useRef<string>(`needs-attention-realtime-${Date.now()}`);

  const fetchItems = useCallback(async () => {
    const { data } = await supabase
      .from('needs_attention_items')
      .select('*')
      .eq('is_resolved', false)
      .order('is_urgent', { ascending: false })
      .order('due_date', { ascending: true });

    setItems(data ?? []);
    setLoading(false);
  }, [supabase]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Real-time subscription
  useEffect(() => {
    const channelName = channelRef.current;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'needs_attention_items' },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const newItem = payload.new as NeedsAttentionItem;
            if (!newItem.is_resolved) {
              setItems((prev) => {
                if (prev.some((i) => i.id === newItem.id)) return prev;
                return [newItem, ...prev];
              });
            }
          } else if (payload.eventType === 'UPDATE') {
            const updated = payload.new as NeedsAttentionItem;
            if (updated.is_resolved) {
              setItems((prev) => prev.filter((i) => i.id !== updated.id));
            } else {
              setItems((prev) =>
                prev.map((i) => (i.id === updated.id ? updated : i))
              );
            }
          } else if (payload.eventType === 'DELETE') {
            const deleted = payload.old as { id: string };
            setItems((prev) => prev.filter((i) => i.id !== deleted.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [supabase]);

  const resolveItem = useCallback(
    async (id: string) => {
      if (!user) return;
      await supabase
        .from('needs_attention_items')
        .update({
          is_resolved: true,
          resolved_at: new Date().toISOString(),
          resolved_by: user.id,
        })
        .eq('id', id);
      // Optimistic update
      setItems((prev) => prev.filter((i) => i.id !== id));
    },
    [user, supabase]
  );

  return { items, loading, resolveItem, refetch: fetchItems };
}
